'use client';

import { useRef, useState, type ReactNode } from 'react';
import Link from 'next/link';

/**
 * "Try one rep" — the live demo question on the landing (sim terminal).
 * One build-time-baked item, no engine, no network (wiki/VISUAL-DESIGN.md §2).
 * Original item authored from official AWS docs facts — never from exam dumps.
 *
 * Feedback follows the juice rules (workspace GAME-MECHANICS.md §5.2): a
 * correct answer gets a small XP float; a wrong answer gets a neutral
 * acknowledgment + the explanation — no punishment juice, ever.
 *
 * The mentor identity arrives via props (server-resolved from the vocabulary
 * seam); this client component never touches the fs-backed loader (K14).
 */

const OPTIONS = [
  { key: 'A', text: 'On-Demand Instances', correct: false },
  { key: 'B', text: 'Spot Instances', correct: true },
  { key: 'C', text: 'Dedicated Hosts', correct: false },
  { key: 'D', text: 'Compute Savings Plans', correct: false },
] as const;

export default function DemoRep({
  mentorName,
  mentorFigure,
}: {
  mentorName: string;
  mentorFigure: ReactNode;
}) {
  const [picked, setPicked] = useState<string | null>(null);
  const [floatPos, setFloatPos] = useState<{ left: number; top: number } | null>(null);
  const bodyRef = useRef<HTMLDivElement | null>(null);

  const answered = picked !== null;
  const pickedCorrect = answered && OPTIONS.find((o) => o.key === picked)?.correct === true;

  function answer(key: string, event: React.MouseEvent<HTMLButtonElement>) {
    if (answered) return;
    setPicked(key);
    const option = OPTIONS.find((o) => o.key === key);
    if (option?.correct && bodyRef.current) {
      const r = event.currentTarget.getBoundingClientRect();
      const p = bodyRef.current.getBoundingClientRect();
      setFloatPos({ left: r.right - p.left - 64, top: r.top - p.top - 6 });
    }
  }

  return (
    <div className="terminal">
      <div className="term-bar">
        <span className="term-label">SIM // LIVE REP</span>
        <span className="term-domain">DOMAIN: BILLING &amp; PRICING</span>
      </div>
      <div className="term-body" ref={bodyRef}>
        <p className="sim-q">
          A workload can tolerate interruptions, and the goal is the lowest possible EC2 compute
          cost. Which purchasing option fits best?
        </p>
        <div className="opts">
          {OPTIONS.map((o) => (
            <button
              key={o.key}
              type="button"
              className={[
                'opt',
                answered && o.correct ? 'correct' : '',
                answered && picked === o.key && !o.correct ? 'picked-wrong' : '',
              ]
                .filter(Boolean)
                .join(' ')}
              disabled={answered}
              onClick={(e) => answer(o.key, e)}
            >
              <span className="opt-key">{o.key}</span>
              {o.text}
            </button>
          ))}
        </div>
        {floatPos && (
          <span className="xp-float" style={{ left: floatPos.left, top: floatPos.top }}>
            +12 XP
          </span>
        )}
        {answered && (
          <div className="verdict-row">
            <div className="verdict-figure" aria-hidden="true">
              {mentorFigure}
            </div>
            <p className="sim-verdict">
              <span className="verdict-who">{mentorName} //</span>{' '}
              {pickedCorrect ? (
                <>
                  Clean signal. Spot is spare capacity at up to 90% off — AWS can reclaim it with
                  a two-minute warning. <strong>Interruption-tolerant → Spot.</strong>
                </>
              ) : (
                <>
                  Not this one — logged, we&apos;ll drill it. <strong>Spot Instances</strong>:
                  spare capacity, up to 90% off, two-minute reclaim warning. That maps to the
                  question&apos;s constraints.
                </>
              )}
            </p>
          </div>
        )}
        {answered && (
          <div className="sim-after">
            <p>This becomes your daily 15 minutes. Scored, spaced, adaptive.</p>
            <Link href="/train" className="btn btn-primary">
              Start training free
            </Link>
          </div>
        )}
      </div>
    </div>
  );
}
